import { AssistChip, Box, ChipGroup } from '@zillow/constellation-10';
import { IconMessageFilled, IconProfileFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useState } from 'react';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';
import { ModalType } from '../../types/ModalType';
import {
  emitDisplayRequestRenterProfileButtonEvent,
  emitRequestRenterProfileClickEvent,
  REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/requestRenterProfile';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseSavedReplyClickEvent,
} from '../../constants/clickstreamEvents/savedReplyModal';
import StringConstants from '../../constants/StringConstants';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { getComposerDisabledText, isComposerDisabled } from '../../utils/composerRestrictions';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import SavedReplyModal from '../Modals/SavedReplyModal';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import useConversationActions from '../../hooks/useConversationActions';
import useLeadRestrictions from '../../hooks/useLeadRestrictions';
import { useAuth } from '../../AuthProvider';
import { compactContainerStyles, defaultContainerStyles } from './styles';

export type MessageListActionsData = {
  disabledFeatures: ConversationMessagesData['disabledFeatures'];
  isPhoneLead: boolean;
  isConversationBlockedByZillow: boolean;
  isSpam: boolean;
  renterName: string;
  shouldShowRequestRenterProfileCta: boolean;
};

export const selectMessageListActionsData = (data: ConversationMessagesData): MessageListActionsData => ({
  disabledFeatures: data.disabledFeatures,
  isPhoneLead: data.isPhoneLead,
  isConversationBlockedByZillow: data.isConversationBlockedByZillow,
  isSpam: data.status.isSpam,
  renterName: data.renterName ?? '',
  shouldShowRequestRenterProfileCta: data.renterProfile.shouldShowRequestRenterProfileCta,
});

type MessageListActionsProps = {
  conversationId: string;
  listingAlias: string;
  leadId?: string | null;
  clientTimeZone: string;
  variant?: 'default' | 'compact';
  triggerLocation: TriggerLocation;
  // The composer's own send gate, passed down by the parent (which also folds in the
  // unloaded-conversation case) so the chip can never be enabled beside a composer that can't send.
  canSendMessage?: boolean;
  sendDisabledMessage?: string;
};

export default function MessageListActions({
  conversationId,
  listingAlias,
  leadId,
  clientTimeZone,
  variant = 'default',
  triggerLocation,
  canSendMessage = true,
  sendDisabledMessage,
}: MessageListActionsProps): JSX.Element {
  const isCompact = variant === 'compact';
  const { isImpersonator } = useContext(AppContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { hasErrorOnRequestRenterProfile, requestRenterProfile } = useConversationActions();
  const [isRequestProfileModalOpen, setIsRequestProfileModalOpen] = useState(false);

  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectMessageListActionsData,
  });

  const { isPhoneLead, isConversationBlockedByZillow, isSpam, renterName, shouldShowRequestRenterProfileCta } =
    conversationData || {};
  const { canRequestRenterProfile } = useLeadRestrictions(conversationData);
  const canShowRequestRenterProfileCta = Boolean(shouldShowRequestRenterProfileCta) && canRequestRenterProfile;
  const composerRestrictionArgs = {
    canSendMessage,
    sendDisabledMessage,
    isImpersonator,
    isPhoneLead: isPhoneLead ?? false,
    isSpam: isSpam ?? false,
    isConversationBlockedByZillow: isConversationBlockedByZillow ?? false,
  };
  const isChooseSavedReplyDisabled = isComposerDisabled(composerRestrictionArgs);

  useEffect(() => {
    if (canShowRequestRenterProfileCta) {
      emitDisplayRequestRenterProfileButtonEvent({
        user,
        listing: { listingAlias },
        triggerLocation,
        lead: { leadId },
      });
    }
  }, [canShowRequestRenterProfileCta, user, listingAlias, leadId, triggerLocation]);

  const chooseSavedReplyTooltipText = getComposerDisabledText(composerRestrictionArgs) ?? '';

  const handleShowSavedReplyModal = (): void => {
    setActiveModal(ModalType.SavedReplyModal);
    emitChooseSavedReplyClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
  };

  const handleShowRequestRenterProfileModal = (): void => {
    // Local state, not the shared `activeModal` slot: routing this modal through that slot double-renders
    // it (messaging modal + page-level LeadManagementModals) and tears down the renter details drawer.
    setIsRequestProfileModalOpen(true);
    emitRequestRenterProfileClickEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
  };

  return (
    <>
      {activeModal === ModalType.SavedReplyModal && (
        <SavedReplyModal listingAlias={listingAlias} leadId={leadId} triggerLocation={triggerLocation} />
      )}

      {isRequestProfileModalOpen && (
        <RequestRenterProfileModal
          name={renterName || ''}
          onRequestRenterProfileClick={() =>
            requestRenterProfile({
              conversationId,
              listingAlias,
              clientTimeZone,
              onSuccess: () => setIsRequestProfileModalOpen(false),
            })
          }
          hasError={hasErrorOnRequestRenterProfile}
          onClose={() => setIsRequestProfileModalOpen(false)}
        />
      )}

      <Box css={isCompact ? compactContainerStyles : defaultContainerStyles}>
        <ChipGroup
          aria-label="Message actions"
          css={{
            flexWrap: 'nowrap',
            '& > *': { flexShrink: 0 },
          }}
        >
          <TooltipWrapper
            tooltipPlacement="top"
            wrapInTooltip={isChooseSavedReplyDisabled}
            tooltipText={chooseSavedReplyTooltipText}
          >
            <AssistChip
              data-eventid={CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID}
              aria-haspopup="true"
              aria-label="View saved reply messages"
              disabled={isChooseSavedReplyDisabled}
              onClick={handleShowSavedReplyModal}
              icon={<IconMessageFilled />}
            >
              Choose saved reply
            </AssistChip>
          </TooltipWrapper>

          {canShowRequestRenterProfileCta && (
            <TooltipWrapper
              tooltipPlacement="top"
              wrapInTooltip={isImpersonator}
              tooltipText={StringConstants.TOOLTIP.IMPERSONATION}
            >
              <AssistChip
                data-eventid={REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID}
                aria-haspopup="true"
                aria-label="Request more information"
                disabled={isImpersonator}
                onClick={handleShowRequestRenterProfileModal}
                icon={<IconProfileFilled />}
              >
                Request more information
              </AssistChip>
            </TooltipWrapper>
          )}
        </ChipGroup>
      </Box>
    </>
  );
}
